import { Component } from './cart-discount-component.js';

console.debug('cart-discount.js module loaded');

function fetchConfig(type = 'json', config = {}) {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': `application/${type}`,
    ...config.headers
  };
  return {
    method: 'POST',
    headers: headers,
    body: config.body,
  };
}

class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorAlreadyApplied', 'cartDiscountSuccess'];

  #activeFetch = null;
  #requestId = 0;
  #feedbackTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    this.#resetFeedbackState();
    this.#bindDiscountForm();
  }

  disconnectedCallback() {
    this.#clearFeedbackTimeout();
    this.#unbindDiscountForm();
    super.disconnectedCallback();
  }

  #formKeydownHandler = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.applyDiscount(event);
  };

  #bindDiscountForm() {
    const form = this.querySelector('.cart-discount__form');
    if (!(form instanceof HTMLElement)) return;

    const input = form.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.removeEventListener('keydown', this.#formKeydownHandler);
    input.addEventListener('keydown', this.#formKeydownHandler);
  }

  #unbindDiscountForm() {
    const form = this.querySelector('.cart-discount__form');
    const input = form?.querySelector?.('input[name="discount"]');
    if (input instanceof HTMLInputElement) {
      input.removeEventListener('keydown', this.#formKeydownHandler);
    }
  }

  #formSubmitHandler = null;

  #resetFeedbackState() {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorAlreadyApplied,
      cartDiscountSuccess
    } = this.refs;

    cartDiscountError?.classList.add('hidden');
    cartDiscountErrorDiscountCode?.classList.add('hidden');
    cartDiscountErrorShipping?.classList.add('hidden');
    cartDiscountErrorAlreadyApplied?.classList.add('hidden');
    cartDiscountSuccess?.classList.add('hidden');
  }

  #clearFeedbackTimeout() {
    if (this.#feedbackTimeout) {
      window.clearTimeout(this.#feedbackTimeout);
      this.#feedbackTimeout = null;
    }
  }

  #scheduleFeedbackReset() {
    this.#clearFeedbackTimeout();
    this.#feedbackTimeout = window.setTimeout(() => {
      this.#resetFeedbackState();
      this.#feedbackTimeout = null;
    }, 5000);
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  applyDiscount = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    if (event?.stopImmediatePropagation) {
      event.stopImmediatePropagation();
    }

    const form = this.querySelector('.cart-discount__form') || 
                 event?.target?.closest?.('.cart-discount__form');
    
    if (!(form instanceof HTMLElement)) {
      return;
    }
    
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorAlreadyApplied,
      cartDiscountSuccess
    } = this.refs;
    
    if (
      !cartDiscountError ||
      !cartDiscountErrorDiscountCode ||
      !cartDiscountErrorShipping ||
      !cartDiscountErrorAlreadyApplied ||
      !cartDiscountSuccess
    ) {
      return;
    }

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) {
      return;
    }
    
    if (typeof this.dataset.sectionId !== 'string') {
      return;
    }

    const discountCodeValue = discountCode.value.trim();
    const normalizedDiscountCodeValue = this.#normalizeDiscountCode(discountCodeValue);
    if (!normalizedDiscountCodeValue) {
      return;
    }
    const requestId = ++this.#requestId;
    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some((code) => this.#normalizeDiscountCode(code) === normalizedDiscountCodeValue)) {
        discountCode.value = '';
        this.#handleDiscountError('already_applied');
        return;
      }

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      cartDiscountErrorAlreadyApplied.classList.add('hidden');
      cartDiscountSuccess.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const cartUpdateUrl = (window.routes?.cartUrl || '/cart') + '/update.js';
      const response = await fetch(cartUpdateUrl, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const data = await response.json();

      if (data.errors) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const appliedDiscount = data.discount_codes?.find((discount) => {
        return discount.code === discountCodeValue;
      });

      if (!appliedDiscount || appliedDiscount.applicable === false) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      discountCode.value = '';

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (newHtml) {
        let section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
        
        if (!section) {
          section = document.querySelector(`[data-section-id="${this.dataset.sectionId}"]`);
        }
        
        if (!section) {
          section = document.getElementById('sidebar-cart')?.closest('[data-section-id]')?.parentElement;
        }
        
        if (section) {
          const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
          const newSection = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`) || 
                            parsedHtml.querySelector(`[data-section-id="${this.dataset.sectionId}"]`);
          const cartDiscountElement = section.querySelector('cart-discount-component')?.closest('.cart-discount')?.parentElement;
          const newCartDiscountElement = parsedHtml.querySelector('cart-discount-component')?.closest('.cart-discount')?.parentElement;
          
          if (cartDiscountElement && newCartDiscountElement) {
            cartDiscountElement.outerHTML = newCartDiscountElement.outerHTML;
          } else {
            section.innerHTML = newHtml;
          }
        }
      }
      
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));

      // Após 1s, confirmar no /cart.js se o cupom segue aplicado.
      // Se outro pedido foi feito depois, esta verificação é ignorada.
      setTimeout(async () => {
        if (requestId !== this.#requestId) return;

        try {
          const cartUrl = window.routes?.cartUrl || '/cart';
          const cartResponse = await fetch(`${cartUrl}.js`, {
            headers: {
              'Accept': 'application/json'
            }
          });
          if (!cartResponse.ok) {
            this.#handleDiscountError('discount_code');
            return;
          }

          const cartData = await cartResponse.json();
          const finalDiscount = cartData.discount_codes?.find((discount) => {
            return discount.code?.toLowerCase?.() === discountCodeValue.toLowerCase?.();
          });

          if (!finalDiscount || finalDiscount.applicable === false) {
            this.#handleDiscountError('discount_code');
            return;
          }

          this.#handleDiscountSuccess();
        } catch (error) {
          console.error('Error verifying discount on cart.js:', error);
          this.#handleDiscountError('discount_code');
        }
      }, 1000);
    } catch (error) {
      console.error('Error applying discount:', error);
    } finally {
      this.#activeFetch = null;
    }
  };

  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    await this.removeDiscountByCode(discountCode);
  };

  removeDiscountByCode = async (discountCode) => {
    if (!discountCode) {
      console.warn('removeDiscountByCode: missing discountCode');
      return;
    }

    if (typeof this.dataset.sectionId !== 'string') {
      console.warn('removeDiscountByCode: missing data-section-id on component');
      return;
    }

    console.debug('removeDiscountByCode: removing code', discountCode);

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const cartUpdateUrl = (window.routes?.cartUrl || '/cart') + '/update.js';
      const response = await fetch(cartUpdateUrl, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (newHtml) {
        let section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
        
        if (!section) {
          section = document.querySelector(`[data-section-id="${this.dataset.sectionId}"]`);
        }
        
        if (!section) {
          section = document.getElementById('sidebar-cart')?.closest('[data-section-id]')?.parentElement;
        }
        
        if (section) {
          const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
          const cartDiscountElement = section.querySelector('cart-discount-component')?.closest('.cart-discount')?.parentElement;
          const newCartDiscountElement = parsedHtml.querySelector('cart-discount-component')?.closest('.cart-discount')?.parentElement;
          
          if (cartDiscountElement && newCartDiscountElement) {
            cartDiscountElement.outerHTML = newCartDiscountElement.outerHTML;
          } else {
            section.innerHTML = newHtml;
          }
        }
      }
      
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));
    } catch (error) {
      console.error('Error removing discount:', error);
    } finally {
      this.#activeFetch = null;
    }
  };

  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorAlreadyApplied,
      cartDiscountSuccess
    } = this.refs;
    const target = {
      discount_code: cartDiscountErrorDiscountCode,
      shipping: cartDiscountErrorShipping,
      already_applied: cartDiscountErrorAlreadyApplied
    }[type] ?? cartDiscountErrorDiscountCode;
    cartDiscountError.classList.remove('hidden');
    cartDiscountErrorDiscountCode?.classList.add('hidden');
    cartDiscountErrorShipping?.classList.add('hidden');
    cartDiscountErrorAlreadyApplied?.classList.add('hidden');
    target.classList.remove('hidden');
    cartDiscountSuccess.classList.add('hidden');
    this.#scheduleFeedbackReset();
  }

  #handleDiscountSuccess() {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorAlreadyApplied,
      cartDiscountSuccess
    } = this.refs;
    if (!cartDiscountError || !cartDiscountSuccess) return;

    cartDiscountError.classList.remove('hidden');
    cartDiscountSuccess.classList.remove('hidden');
    cartDiscountErrorDiscountCode?.classList.add('hidden');
    cartDiscountErrorShipping?.classList.add('hidden');
    cartDiscountErrorAlreadyApplied?.classList.add('hidden');
    this.#scheduleFeedbackReset();
  }

  #normalizeDiscountCode(code) {
    return String(code ?? '').trim().toLowerCase();
  }

  #existingDiscounts() {
    const codesSet = new Set();

    // Pills dentro do próprio componente (caso existam)
    const componentPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of componentPills) {
      if (pill instanceof HTMLElement && typeof pill.dataset.discountCode === 'string') {
        codesSet.add(pill.dataset.discountCode);
      }
    }

    // Pills no header de desconto (disclosure-trigger)
    const headerPills = document.querySelectorAll('.disclosure-trigger__pill');
    for (const pill of headerPills) {
      if (pill instanceof HTMLElement && typeof pill.dataset.discountCode === 'string') {
        codesSet.add(pill.dataset.discountCode);
      }
    }

    return Array.from(codesSet);
  }

  getExistingDiscounts() {
    return this.#existingDiscounts();
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

document.addEventListener('submit', (event) => {
  const form = event.target;

  // Discount UI is no longer a <form> (nested forms break Safari). Keep this
  // guard in case older markup is still cached.
  if (form instanceof HTMLFormElement && form.classList.contains('cart-discount__form')) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    const component = form.closest('cart-discount-component');
    if (component instanceof CartDiscount) {
      component.applyDiscount(event);
    }
    return;
  }
  
  if (form instanceof HTMLFormElement && form.classList.contains('Cart') && form.classList.contains('Drawer__Content')) {
    const checkoutButton = event.submitter;
    if (checkoutButton && checkoutButton.name === 'checkout') {
      const discountComponent = form.querySelector('cart-discount-component');
      if (discountComponent instanceof CartDiscount) {
        const existingDiscounts = discountComponent.getExistingDiscounts();
        if (existingDiscounts.length > 0) {
          let discountInput = form.querySelector('input[name="discount"]');
          if (!discountInput) {
            discountInput = document.createElement('input');
            discountInput.type = 'hidden';
            discountInput.name = 'discount';
            form.appendChild(discountInput);
          }
          discountInput.value = existingDiscounts.join(',');
        }
      }
    }
  }
}, { capture: true });

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const button = target.closest('[data-remove-discount-code]');
  if (!button) return;

  const code = button.getAttribute('data-remove-discount-code');
  if (!code) return;

  console.debug('cart-discount: header remove click', { code });

  event.preventDefault();
  event.stopPropagation();

  const component = document.querySelector('cart-discount-component');
  if (component instanceof CartDiscount) {
    component.removeDiscountByCode(code);
  }
}, { capture: true });
